import React, { useMemo } from 'react';
import { getAncestorPedigree } from '../utils/ancestorUtils';
import './AncestorPedigreeView.css';

/**
 * AncestorPedigreeView component
 * Renders a horizontal pedigree chart showing a person's direct ancestors.
 * Person at left, parents to the right, grandparents further right.
 *
 * Props:
 * - gedcomData: Object - Full parsed GEDCOM data { individuals, families }
 * - personId: string - ID of the person to show ancestors for
 * - maxGenerations: number - Max depth (default: 4, max: 6, min: 4 when data available)
 * - onPersonClick: (id) => void - Navigation callback when an ancestor is clicked
 */
const AncestorPedigreeView = ({ gedcomData, personId, maxGenerations = 4, onPersonClick }) => {
  const pedigreeData = useMemo(() => {
    if (!gedcomData || !personId) return null;
    // Clamp generations between 4 and 6
    const generations = Math.min(6, Math.max(4, maxGenerations));
    return getAncestorPedigree(gedcomData, personId, generations);
  }, [gedcomData, personId, maxGenerations]);

  // If no data at all (person not found)
  if (!pedigreeData) {
    return (
      <div className="pedigree-view" data-testid="pedigree-view">
        <p className="pedigree-no-data" data-testid="pedigree-no-data">
          No ancestor data is available for this person
        </p>
      </div>
    );
  }

  // Check if person has NO parent data at all
  const hasAnyParent = pedigreeData.father !== null || pedigreeData.mother !== null;

  if (!hasAnyParent) {
    return (
      <div className="pedigree-view" data-testid="pedigree-view">
        <p className="pedigree-no-data" data-testid="pedigree-no-data">
          No ancestor data is available for this person
        </p>
      </div>
    );
  }

  // Determine actual generations to display (min 4, max 6)
  const effectiveGenerations = Math.min(6, Math.max(4, maxGenerations));
  const totalRows = Math.pow(2, effectiveGenerations - 1);

  // Flatten the tree into positioned nodes for the grid
  const nodes = [];
  flattenTree(pedigreeData, 0, 0, totalRows - 1, nodes, effectiveGenerations);

  return (
    <div className="pedigree-view" data-testid="pedigree-view">
      <div
        className="pedigree-container"
        data-testid="pedigree-container"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${effectiveGenerations}, 1fr)`,
          gridTemplateRows: `repeat(${totalRows}, 1fr)`
        }}
      >
        {nodes.map((node, index) => (
          <PedigreeNode
            key={`${node.col}-${node.rowStart}-${index}`}
            node={node}
            onPersonClick={onPersonClick}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Recursively flatten the pedigree tree into grid-positioned nodes.
 */
function flattenTree(node, col, rowStart, rowEnd, nodes, maxGen) {
  if (!node || col >= maxGen) return;

  const rowMid = Math.floor((rowStart + rowEnd) / 2);

  nodes.push({
    person: node.person,
    generation: node.generation,
    col: col + 1,
    rowStart: rowMid + 1,
    rowEnd: rowMid + 1,
    isPlaceholder: false
  });

  if (col + 1 < maxGen) {
    const midPoint = Math.floor((rowStart + rowEnd) / 2);

    // Father (top half)
    if (node.father) {
      flattenTree(node.father, col + 1, rowStart, midPoint, nodes, maxGen);
    } else {
      // Placeholder for missing father
      const placeholderMid = Math.floor((rowStart + midPoint) / 2);
      nodes.push({
        person: null,
        generation: node.generation + 1,
        col: col + 2,
        rowStart: placeholderMid + 1,
        rowEnd: placeholderMid + 1,
        isPlaceholder: true
      });
    }

    // Mother (bottom half)
    if (node.mother) {
      flattenTree(node.mother, col + 1, midPoint + 1, rowEnd, nodes, maxGen);
    } else {
      // Placeholder for missing mother
      const placeholderMid = Math.floor((midPoint + 1 + rowEnd) / 2);
      nodes.push({
        person: null,
        generation: node.generation + 1,
        col: col + 2,
        rowStart: placeholderMid + 1,
        rowEnd: placeholderMid + 1,
        isPlaceholder: true
      });
    }
  }
}

/**
 * PedigreeNode renders a single ancestor node or placeholder in the grid.
 */
const PedigreeNode = ({ node, onPersonClick }) => {
  const { person, col, rowStart, isPlaceholder } = node;

  const style = {
    gridColumn: col,
    gridRow: rowStart
  };

  if (isPlaceholder) {
    return (
      <div
        className="pedigree-node pedigree-node-placeholder"
        style={style}
        data-testid="pedigree-placeholder"
        aria-label="Unknown ancestor"
      >
        <span className="pedigree-node-name">Unknown</span>
      </div>
    );
  }

  const name = person?.name || 'Unknown';
  const birthYear = extractYear(person?.birth);
  const deathYear = extractYear(person?.death);
  const yearsDisplay = formatYears(birthYear, deathYear);

  const handleClick = () => {
    if (person?.id && onPersonClick) {
      onPersonClick(person.id);
    }
  };

  return (
    <button
      className="pedigree-node pedigree-node-ancestor"
      style={style}
      onClick={handleClick}
      type="button"
      data-testid="pedigree-ancestor"
      aria-label={`View details for ${name}`}
    >
      <span className="pedigree-node-name">{name}</span>
      {yearsDisplay && (
        <span className="pedigree-node-years">{yearsDisplay}</span>
      )}
    </button>
  );
};

/**
 * Extract a 4-digit year from a date string
 */
function extractYear(dateStr) {
  if (!dateStr) return '';
  const match = dateStr.match(/\d{4}/);
  return match ? match[0] : '';
}

/**
 * Format birth/death years for display
 */
function formatYears(birthYear, deathYear) {
  if (birthYear && deathYear) return `${birthYear}–${deathYear}`;
  if (birthYear) return `b. ${birthYear}`;
  if (deathYear) return `d. ${deathYear}`;
  return '';
}

export default AncestorPedigreeView;
